import { Box, Button, Stack, Typography } from "@mui/material";
import { TransitionModal } from "components/modal";
import IdentityDocumentForm from "./IdentityDocumentForm";
import { useRejectDocument } from "core/documents/hooks";
import { useApproveDocument } from "core/documents/hooks";
import { toast } from "react-toastify";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ResidencyProofDocumentForm from "./ResidencyProofDocumentForm";
import { OpenInNew } from "@mui/icons-material";
import SSNCardDocumentForm from "./SSNCardDocumentForm";
import BankDetailsDocumentForm from "./BankDetailsDocumentForm";
import SimpleValidationDocumentForm from "./SimpleValidationDocumentForm";
import DocumentOcrStatus from "./DocumentOcrStatus";
import PdfPreview from "components/PdfPreview";
import { useState } from "react";
import RejectDocumentDialog from "./RejectDocumentDialog";

type ValidationModalProps = {
  open: boolean;
  onClose: () => void;
  document: any;
  tempWorkerId: number;
};

export type DocumentFormProps = {
  document: any;
  onApprove: (data: any) => void;
  onReject: () => void;
  disabled: boolean;
};

const Forms: { [key: string]: React.ComponentType<DocumentFormProps> } = {
  identity: IdentityDocumentForm,
  residency_proof: ResidencyProofDocumentForm,
  ssn_card: SSNCardDocumentForm,
  bank_details: BankDetailsDocumentForm,
  resume: SimpleValidationDocumentForm,
  driver_license: SimpleValidationDocumentForm,
  medical_visit: SimpleValidationDocumentForm,
}

const ValidationModal = ({ open, onClose, document, tempWorkerId }: ValidationModalProps) => {
  // Fallback so an unmapped document key never renders an undefined component (blank page)
  const Form = Forms[document.key as keyof typeof Forms] ?? SimpleValidationDocumentForm;
  const { confirmationDialog, confirm, closeConfirmationDialog } = useConfirmationDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const approveDocumentMutation = useApproveDocument(tempWorkerId, {
    onSuccess: () => toast.success('Document validé'),
    onError: () => toast.error('Une erreur est survenue'),
  });
  const rejectDocumentMutation = useRejectDocument(tempWorkerId, {
    onSuccess: () => {
      setRejectDialogOpen(false);
      onClose();
      toast.success('Document refusé');
    },
    onError: () => toast.error('Une erreur est survenue'),
  });

  const onApprove = (data: any) => {
    confirm({
      title: 'Confirmation',
      description: 'Voulez-vous valider le document ? Assurez-vous que le formulaire est conforme.',
      onConfirm: () => {
        closeConfirmationDialog();
        onClose();
        approveDocumentMutation.mutate({
          tempWorkerId: tempWorkerId,
          documentId: document.id,
          ocrData: data,
        });
      },
    });
  };

  const onReject = () => {
    setRejectDialogOpen(true);
  };

  const onRejectConfirm = (rejectionReason: string) => {
    rejectDocumentMutation.mutate({
      tempWorkerId: tempWorkerId,
      documentId: document.id,
      rejectionReason,
    });
  };

  return (
    <>
      <TransitionModal
        open={open}
        onClose={onClose}
        fullWidth
        fullHeight={false}
        maxWidth="lg"
      >
        {confirmationDialog}
        <Stack direction="row">
          <Box sx={{
            width: '600px',
            height: '100%',
            backgroundColor: 'white',
            gap: 2,
            py: 2,
            overflowY: 'scroll',
          }}>
            {document.files.map((file: any) => {
              if (file.url.toLowerCase().includes('heic')) {
                return (
                  <Box key={file.id} sx={{ mt: 2, ml: 2 }}>
                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="contained" color="info" startIcon={<OpenInNew />}>
                        <Typography variant="body1" sx={{ textDecoration: 'none' }}>Voir le document</Typography>
                      </Button>
                    </a>
                  </Box>
                )
              } else if (file.url.toLowerCase().includes('pdf')) {
                return (
                  <PdfPreview fileUrl={file.url} key={file.id} />
                )
              } else {
                return (
                  <Box key={file.id} sx={{ p: 6 }}>
                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                      <img src={file.url} alt={file.name} width={500} style={{ borderRadius: '10px' }} />
                    </a>
                  </Box>
                )
              }
            })}
          </Box>
          <Box sx={{ width: '600px', backgroundColor: '#F8FAFC', p: 8, pb: 16 }}>
            {document.files.length > 0 && document.validation_state === 'pending' && <DocumentOcrStatus document={document} />}
            <Form document={document} onApprove={onApprove} onReject={onReject} disabled={document.validation_state !== 'pending'} />
          </Box>
        </Stack>
      </TransitionModal>
      <RejectDocumentDialog
        open={rejectDialogOpen}
        loading={rejectDocumentMutation.isLoading}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={onRejectConfirm}
      />
    </>
  );
};

export default ValidationModal;
